import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';

// Virtual Restaurant Attender — admin toggle. When enabled, the user-app home
// shows the conversational food concierge in place of the banner carousel.
function AttenderManagement() {
  const [cfg, setCfg] = useState({ enabled: false, useAI: true, speak: true, title: 'Your food concierge', cities: [] });
  const [cities, setCities] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getAttenderSettings();
      const d = res.data?.data || {};
      setCfg({ enabled: !!d.enabled, useAI: d.useAI !== false, speak: d.speak !== false, title: d.title || 'Your food concierge', cities: d.cities || [] });
      setCities((d.cities || []).join(', '));
      setErr('');
    } catch (e) { setErr('Failed to load settings'); }
    finally { setLoading(false); }
  };

  const save = async () => {
    setSaving(true); setMsg(''); setErr('');
    try {
      const cityList = cities.split(',').map((s) => s.trim()).filter(Boolean);
      const res = await adminAPI.updateAttenderSettings({ ...cfg, cities: cityList });
      const d = res.data?.data || {};
      setCfg({ enabled: !!d.enabled, useAI: d.useAI !== false, speak: d.speak !== false, title: d.title, cities: d.cities || [] });
      setMsg('Saved ✓');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setErr('Failed: ' + (e.response?.data?.message || e.message)); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={s.wrap}><p style={{ color: '#64748B' }}>Loading…</p></div>;

  return (
    <div style={s.wrap}>
      <h1 style={s.title}>Virtual Restaurant Attender</h1>
      <p style={s.sub}>A warm, conversational food concierge on the user-app home. When enabled, it <b>replaces the banner carousel</b> — greeting customers (Namaste), asking their cuisine/mood, and suggesting top items across all restaurants.</p>

      <div style={s.card}>
        <label style={s.row}>
          <span><b>Enable attender</b><div style={s.hint}>Off = users see the normal banner carousel.</div></span>
          <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} style={s.chk} />
        </label>
        <label style={s.row}>
          <span><b>Use AI (Claude)</b><div style={s.hint}>Warm greetings, free-text craving understanding, per-item reasons. Off = templates (still works).</div></span>
          <input type="checkbox" checked={cfg.useAI} onChange={(e) => setCfg({ ...cfg, useAI: e.target.checked })} style={s.chk} />
        </label>
        <label style={s.row}>
          <span><b>Voice (animated host speaks)</b><div style={s.hint}>The host reads the greeting aloud (on-device TTS). Users can still mute it. Off = silent, text only.</div></span>
          <input type="checkbox" checked={cfg.speak} onChange={(e) => setCfg({ ...cfg, speak: e.target.checked })} style={s.chk} />
        </label>
        <div style={{ ...s.row, display: 'block' }}>
          <div><b>Title</b><div style={s.hint}>Small heading shown above the attender.</div></div>
          <input value={cfg.title} onChange={(e) => setCfg({ ...cfg, title: e.target.value })} style={s.input} maxLength={60} />
        </div>
        <div style={{ ...s.row, display: 'block', borderBottom: 'none' }}>
          <div><b>Cities</b><div style={s.hint}>Comma-separated cityIds to limit it to specific cities. Leave blank = all cities.</div></div>
          <input value={cities} onChange={(e) => setCities(e.target.value)} placeholder="(all cities)" style={s.input} />
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16 }}>
          <button onClick={save} disabled={saving} style={s.btn}>{saving ? 'Saving…' : 'Save'}</button>
          {msg ? <span style={{ color: '#166534', fontWeight: 600, fontSize: 13 }}>{msg}</span> : null}
          {err ? <span style={{ color: '#DC2626', fontSize: 13 }}>{err}</span> : null}
        </div>
      </div>

      <div style={s.note}>
        <b>Note:</b> The attender UI ships in the user app (needs an app release). This toggle controls whether it appears once that build is live; suggestions come from delivered-item popularity, ratings, and the customer's craving.
      </div>
    </div>
  );
}

const s = {
  wrap: { maxWidth: 720, margin: '0 auto', padding: 24, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  title: { fontSize: 24, fontWeight: 800, color: '#0F172A', margin: '0 0 6px' },
  sub: { color: '#64748B', fontSize: 14, margin: '0 0 18px', lineHeight: 1.5 },
  card: { background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 20 },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, padding: '14px 0', borderBottom: '1px solid #F1F5F9' },
  hint: { fontSize: 12, color: '#94A3B8', marginTop: 4, fontWeight: 400 },
  chk: { width: 20, height: 20, cursor: 'pointer', flexShrink: 0 },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginTop: 8 },
  btn: { background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  note: { marginTop: 16, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: 14, fontSize: 13, color: '#92400E', lineHeight: 1.5 },
};

export default AttenderManagement;
